import { QueryClient } from '@tanstack/react-query';

import { ApiError } from './client.ts';

/**
 * TanStack Query holds all server state. Nothing fetched from the API lives in `useState`.
 *
 * `retry` is a function rather than a flat `false` or `true`. A 4xx is never transient — a 401, a
 * 403, a 404 and a contract mismatch are all answers, not failures to reach an answer, so retrying
 * one wastes a round trip and delays the real response. But a genuine network drop on a ward's
 * flaky wifi is worth one retry, and blanket `retry: false` gives that case nothing.
 *
 * `refetchOnWindowFocus` is on for the session query only (see `useSession`), not globally. A
 * clinician returning to a tab after an idle timeout should find out immediately rather than on
 * their next click; a list of records should not silently re-fetch under them.
 */
export function createQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        retry: (failureCount, error) => {
          if (error instanceof ApiError) {
            // Status 0 is "never reached a server" — the only genuinely transient case.
            return error.status === 0 && failureCount < 2;
          }
          return failureCount < 1;
        },
        refetchOnWindowFocus: false,
        staleTime: 30_000,
      },
      mutations: {
        retry: false,
      },
    },
  });
}
